import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { convertDatesInObject } from '@/helpers/api'

import { organizationsQueryKeys } from '@/hooks/organization/types'

import type { UUID } from '@/types/Id'
import type { Organization } from '@/types/Organization'

const fetchOrganization = async (
  orgId: string,
  accessToken: string | undefined | null,
): Promise<Organization> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }
  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  return convertDatesInObject(await res.json())
}

interface UseOrganizationOptions<T> {
  select?: (data: Organization) => T
}

export const useOrganization = <T = Organization>(
  orgId: string,
  opts?: UseOrganizationOptions<T>,
) => {
  const { accessToken } = useAuthInfo()

  const selectFn = useCallback(
    (data: Organization) => {
      if (opts?.select) {
        return opts.select(data)
      }
      return data as unknown as T
    },
    [opts?.select],
  )

  return useQuery({
    queryFn: () => fetchOrganization(orgId, accessToken),
    queryKey: organizationsQueryKeys.organization({ orgId: orgId as UUID }),
    staleTime: 60000, // 60 seconds
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: orgId !== '' && accessToken !== null && accessToken !== undefined,
    select: selectFn,
  })
}
